import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { workExperienceApi } from '../services/backendApi';
import type { WorkExperience, WorkExperienceFormData } from '../types';

interface UseWorkExperienceReturn {
  workExperience: WorkExperience[];
  loading: boolean;
  error: string | null;
  fetchWorkExperience: () => Promise<void>;
  createWorkExperience: (data: WorkExperienceFormData) => Promise<{ success: boolean; error?: string }>;
  updateWorkExperience: (id: string, data: Partial<WorkExperienceFormData>) => Promise<{ success: boolean; error?: string }>;
  deleteWorkExperience: (id: string) => Promise<{ success: boolean; error?: string }>;
  clearError: () => void;
}

export const useWorkExperience = (): UseWorkExperienceReturn => {
  const { isAuthenticated } = useAuth();
  const [workExperience, setWorkExperience] = useState<WorkExperience[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkExperience = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const data = await workExperienceApi.getAll();
      setWorkExperience(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load work experience');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchWorkExperience();
  }, [fetchWorkExperience]);

  const createWorkExperience = useCallback(async (data: WorkExperienceFormData) => {
    setLoading(true);
    setError(null);

    try {
      const created = await workExperienceApi.create(data);
      setWorkExperience(prev => [created, ...prev]);
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create work experience';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateWorkExperience = useCallback(async (id: string, data: Partial<WorkExperienceFormData>) => {
    setLoading(true);
    setError(null);

    try {
      const updated = await workExperienceApi.update(id, data);
      setWorkExperience(prev => prev.map(e => (e.id === id ? updated : e)));
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update work experience';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteWorkExperience = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      await workExperienceApi.remove(id);
      setWorkExperience(prev => prev.filter(e => e.id !== id));
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete work experience';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    workExperience,
    loading,
    error,
    fetchWorkExperience,
    createWorkExperience,
    updateWorkExperience,
    deleteWorkExperience,
    clearError,
  };
};
